import React, { useState, useCallback, useEffect, useContext } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { NavContext } from 'components/privateRoute/PrivateRoute';
import moment from 'moment';
import * as _ from 'lodash';
import AdminHeader from 'components/header';
import LayoutContainer from 'components/layout/LayoutContainer';
import Card from 'components/inputs/Card';
import Button from 'components/inputs/Button';
import DateTimePicker from 'components/inputs/DateTimePicker';
import SearchBox from 'components/general/SearchBox';
import Footer from 'components/general/Footer';
import ReactPagination from 'components/general/ReactPagination';
import { BsSearch } from 'react-icons/bs';
import { IoSearchOutline } from 'react-icons/io5';
import { useSelector, useDispatch } from 'react-redux';
import { RATING_AGGREGATION } from 'actions/rating';


const StationAnalysis = () => {
  const { t } = useTranslation();
  const { navigateTo } = useContext(NavContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const profileData = useSelector((state) => state.profile.userProfile);
  const isLoading = useSelector((state) => state.rating.isAggregationLoading);
  const aggregation = useSelector((state) => state.rating.aggregation);
  const currentUserName = profileData?.name || '';

  const passedFrom = location.state?.from;
  const passedTo = location.state?.to;

  const [startDate, setStartDate] = useState(passedFrom ? moment(passedFrom) : moment().startOf('day'));
  const [endDate, setEndDate] = useState(passedTo ? moment(passedTo) : moment().endOf('day'));
  const [appliedRange, setAppliedRange] = useState(passedFrom ? { from: moment(passedFrom), to: moment(passedTo) } : null);
  const [stationSearch, setStationSearch] = useState('');
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 10;

  const stationData = aggregation && Array.isArray(aggregation.results) ? aggregation : null;

  const fetchData = useCallback((page = 1, search = stationSearch) => {
    setAppliedRange({ from: startDate, to: endDate });
    dispatch({
      type: RATING_AGGREGATION.REQUEST,
      payload: {
        type: 'station',
        from: moment(startDate).utc(),
        to: moment(endDate).utc(),
        page,
        limit,
        sortBy: '-totalIssues',
        ...(search && { search }),
      },
    });
  }, [startDate, endDate, stationSearch]);

  useEffect(() => {
    if (passedFrom) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    fetchData(1, '');
  }, []);

  const applyFilter = useCallback(() => {
    setCurrentPage(1);
    fetchData(1, stationSearch);
  }, [fetchData, stationSearch]);

  const handlePageClick = useCallback((pageVal) => {
    const newPage = pageVal.selected + 1;
    setCurrentPage(newPage);
    fetchData(newPage, stationSearch);
  }, [fetchData, stationSearch]);

  const searchBar = (
    <SearchBox
      preIcon={<BsSearch />}
      value={stationSearch}
      onChange={(e) => setStationSearch(e.target.value)}
      onKeyPress={(e) => {
        if (e.key === 'Enter') applyFilter();
        if (e.key === 'Escape') { setShowSearchBar(false); setStationSearch(''); }
      }}
    />
  );
  const searchIcon = <IoSearchOutline size={27} onClick={() => setShowSearchBar(true)} className="report-search-icon" style={{ cursor: 'pointer' }} />;

  return (
    <LayoutContainer>
      <AdminHeader
        title={t('ratingReview.stationAnalysis')}
        backTo="/ratingReviewReport"
        onBack={() => navigateTo('/ratingReviewReport', { state: { from: moment(startDate).toISOString(), to: moment(endDate).toISOString() } })}
      />
      <div className="page-content-wrapper scrollable">
        <div className="station-analysis--main">
          <Card>
            <div className="station-analysis__filter">
              <Row className="align-items-center" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                <Col xs="auto">
                  {showSearchBar ? searchBar : searchIcon}
                </Col>
                <Col xs="auto">
                  <DateTimePicker
                    onChangeOfStartDate={(item) => setStartDate(item)}
                    onChangeOfEndDate={(item) => setEndDate(item)}
                    initialValueOfStartDate={startDate}
                    initialValueOfEndDate={endDate}
                  />
                </Col>
                <Col xs="auto">
                  <Button className="report-search-button" onClick={applyFilter}>
                    <IoSearchOutline />
                    <span>{t('button.search')}</span>
                  </Button>
                </Col>
                {appliedRange && (
                  <Col xs="auto" className="ms-auto station-analysis__range-label">
                    {`Date Range: ${moment(appliedRange.from).format('YYYY-MM-DD')} to ${moment(appliedRange.to).format('YYYY-MM-DD')}`}
                  </Col>
                )}
              </Row>
            </div>
          </Card>

          {stationData && (
            <div className="station-analysis__summary">
              <div className="station-analysis__stat">
                <span className="station-analysis__stat-label">Total Stations</span>
                <span className="station-analysis__stat-value" style={{ color: '#3c7cdd' }}>{stationData.totalStations ?? 0}</span>
              </div>
              <div className="station-analysis__stat">
                <span className="station-analysis__stat-label">Total Issues / Tags</span>
                <span className="station-analysis__stat-value" style={{ color: '#BE210B' }}>{stationData.totalIssues ?? 0}</span>
              </div>
              <div className="station-analysis__stat">
                <span className="station-analysis__stat-label">Avg Issues / Station</span>
                <span className="station-analysis__stat-value" style={{ color: '#1a9c45' }}>{stationData.avgIssuesPerStation ?? 0}</span>
              </div>
            </div>
          )}

          {stationData && (
            <Card>
              <div className="table-responsive">
                <table className="record-list-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Station Name</th>
                      <th>Total Issues / Tags</th>
                      <th>Issues / Tags Breakdown</th>
                    </tr>
                  </thead>
                  <tbody>
                    {_.isEmpty(stationData.results) ? (
                      <tr>
                        <td colSpan={4} className="border-0">
                          <div className="empty-data-block">No station data found</div>
                        </td>
                      </tr>
                    ) : (
                      stationData.results.map((station, idx) => {
                        const rank = (currentPage - 1) * limit + idx + 1;
                        return (
                          <tr key={station.id ?? idx}>
                            <td><span className="station-analysis__rank">#{rank}</span></td>
                            <td>{station.name}</td>
                            <td><span className="station-analysis__issue-badge">{station.totalIssues}</span></td>
                            <td>
                              {_.isEmpty(station.tags) ? (
                                <span className="station-analysis__no-tags">No tagged issues</span>
                              ) : (
                                <div className="station-analysis__tag-chips">
                                  {Object.entries(station.tags).map(([tag, count]) => (
                                    <span
                                      key={tag}
                                      className="station-analysis__tag-chip"
                                    >
                                      {tag}: {count}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              {stationData.totalPages > 1 && (
                <ReactPagination
                  currentPage={currentPage}
                  limit={limit}
                  total={stationData.totalResults}
                  handlePageClick={handlePageClick}
                  totalPages={stationData.totalPages}
                  marginPagesDisplayed={1}
                />
              )}
            </Card>
          )}

          {!stationData && isLoading && (
            <Card>
              <div className="station-analysis__empty">Loading...</div>
            </Card>
          )}
        </div>
      </div>
      {currentUserName === 'CZ Tenant Admin' ? null : <Footer />}
    </LayoutContainer>
  );
};

export default StationAnalysis;
